import { SideNavService } from './../_services/sidenav.service';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import {AccountService, AlertService, MouseflowService, ReportsConfigService, StorageService, UserService} from '../_services';
import { Pager, User, Account } from '../_models';
import { Form } from '../_forms';
import { filter, takeUntil } from 'rxjs/operators';
import { ReportsUserConfiguration } from '../_models/reports';
import { ReportTypes } from '../reports/models/reports';
import {environment} from '../../environments/environment';
import {
  ButtonItems,
  DynamicChildrenKey,
  HomeMenu,
  MenuId,
  NavContext,
  NavItem,
  ProductEntries,
  ProductsSectionLabel,
  ProductMenu,
} from './nav-config';
import {
  filterNavItems,
  getProductMenu,
  menuClaimsUrl,
  menuForUrl,
  resolveLabel,
  resolveRoute,
  routePath,
  urlIsUnder,
} from './nav-visibility';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SidebarComponent extends Form implements OnInit {
  user: User;

  public isExpanded: boolean = window.outerWidth > 1183;
  public reportsUserConfig: ReportsUserConfiguration[] = [];
  public legacyReportsUserConfig: ReportsUserConfiguration[] = [];
  public legacyReportsGlobalConfig: ReportsUserConfiguration[] = [];
  public isReportsConfigLoading: boolean = true;
  selectedDropdown = '';

  reportsLinkConfig = [
    { report_type: ReportTypes.REFUND, routerLink: '/refund-reports/' },
    {
      report_type: ReportTypes.PAYMENT_UPDATER,
      routerLink: '/payment-updater-reports/',
    },
    {
      report_type: ReportTypes.RETURN_RETENTION,
      routerLink: '/return-retention-reports/',
    },
    {
      report_type: ReportTypes.ORDER_RETENTION,
      routerLink: '/new-order-retention-reports/',
    },
    {
      report_type: ReportTypes.SUBSCRIPTION_RETENTION,
      routerLink: '/subscription-retention-reports/',
    },
    { report_type: ReportTypes.CHARGEBACK, routerLink: '/chargeback-reports/' },
    {
      report_type: ReportTypes.SP_CHARGEBACK,
      routerLink: '/sp-touched-reports/',
    },
    {
      report_type: ReportTypes.CHATBOT,
      routerLink: '/chatbot-reports/',
    },
    {
      report_type: ReportTypes.CUSTOMER_SUPPORT,
      routerLink: '/customer-support-report/',
    },
    {
      report_type: ReportTypes.RESOLVE,
      routerLink: '/resolve-report/',
    },
  ];

  canDisplayMouseflowToggle: boolean = !!environment.mouseflowId

  public navItems: NavItem[] = [];

  public activeMenu: MenuId = MenuId.Home;
  public productEntries: NavItem[] = [];
  public bottomItems: NavItem[] = [];
  public productsSectionLabel = ProductsSectionLabel;

  // Screens that take over the display ask for the collapse control to be hidden.
  public showToggle = true;

  public resolveLabel = resolveLabel;
  public resolveRoute = resolveRoute;

  constructor(
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    private userService: UserService,
    protected sideNavService: SideNavService,
    protected reportsConfigService: ReportsConfigService,
    private storageService: StorageService,
    protected accountService: AccountService,
    protected mouseflowService: MouseflowService
  ) {
    super(alertService, router, location);
  }

  dropdownStylesApplied: string = '';
  subDropdownStylesApplied: string = '';

  ngOnInit() {
    super.ngOnInit();
    this.sideNavService.setSideNavExpanded(this.isExpanded);
    this.syncMenuToUrl(this.router.url);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe((event: NavigationEnd) => {
        this.syncMenuToUrl(event.urlAfterRedirects);
      });

    this.userService
      .getCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (user: User) => {
          this.user = user;
          this.buildNavItems();

          this.reportsConfigService
            .list({})
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              (data: Pager) => {
                this.reportsConfigService._reportConfigSubject.next(data.results);
              },
              (error) => {
                this.isReportsConfigLoading = false;
                this.alertService.error(error.message);
              }
            );

          this.reportsConfigService.reportConfig$.subscribe((configs) => {
            if (configs.length) {
              this.isReportsConfigLoading = true;
              this.legacyReportsGlobalConfig = [];
              this.reportsUserConfig = [];
              this.legacyReportsUserConfig = [];

              configs.forEach((config: ReportsUserConfiguration) => {
                if (this.user && config.user === this.user.id && !config.is_global) {
                  if (config.legacy) {
                    this.legacyReportsUserConfig.push(config);
                  } else {
                    // An unmapped report type is skipped; without this the loop throws and My Reports never loads.
                    const linkConfig = this.reportsLinkConfig.find((x) => x.report_type === config.report_type);
                    if (linkConfig) {
                      config.link = linkConfig.routerLink;
                      this.reportsUserConfig.push(config);
                    }
                  }
                } else if (config.is_global && config.legacy) {
                  this.legacyReportsGlobalConfig.push(config);
                }
              });
              this.isReportsConfigLoading = false;
            }
          });

          if (this.isMouseflowEnabled()) {
            this.mouseflowService.enable(user)
          }
        },
        (error) => {
          this.alertService.error(error.message);
        }
      );

    this.sideNavService.getSideNavObs().subscribe((data) => {
      if ('isSideNavExpanded' in data) {
        this.isExpanded = data.isSideNavExpanded;
        if (!data.isSideNavExpanded) {
          this.selectedDropdown = '';
        }
      }

      if ('showSideNavToggle' in data) {
        this.showToggle = data.showSideNavToggle;
      }
    });

    this.accountService.selectedAccount$.subscribe((account: Account) => {
      if (this.user) {
        this.user.account = account;
        this.buildNavItems();
      }
    });
  }

  get navContext(): NavContext {
    let isStaff = false;
    let hasAccount = false;
    let accountId: number | string = '';

    if (this.user) {
      isStaff = this.user.is_staff;

      if (this.user.account) {
        hasAccount = true;
        accountId = this.user.account.id;
      }
    }

    return {
      hasUser: !!this.user,
      isStaff: isStaff,
      hasAccount: hasAccount,
      accountId: accountId,
      profilerEnabled: !!this.isProfilerEnabled(),
      cprofilerEnabled: !!this.isCProfilerEnabled(),
      mouseflowAvailable: this.canDisplayMouseflowToggle,
      mouseflowEnabled: !!this.isMouseflowEnabled(),
    };
  }

  // Hiding the toggle while collapsed would leave no way to reopen the menu.
  get canToggle(): boolean {
    return this.showToggle || !this.isExpanded;
  }

  get activeProductMenu(): ProductMenu {
    let menu: ProductMenu = null;

    if (this.activeMenu !== MenuId.Home) {
      menu = getProductMenu(this.activeMenu);
    }

    return menu;
  }

  buildNavItems() {
    const ctx = this.navContext;
    let items = HomeMenu.items;

    if (this.activeMenu !== MenuId.Home) {
      const menu = getProductMenu(this.activeMenu);
      items = [];

      if (menu) {
        items = menu.items;
      }
    }

    this.navItems = filterNavItems(items, ctx);
    this.productEntries = [];

    let bottom = filterNavItems(ButtonItems, ctx);

    if (this.activeMenu !== MenuId.Home) {
      bottom = bottom.filter((item: NavItem) => item.homeOnly !== true);
    }

    this.bottomItems = bottom;

    if (this.activeMenu === MenuId.Home) {
      this.productEntries = filterNavItems(ProductEntries, ctx);
    }
  }

  openMenu(id: MenuId) {
    this.activeMenu = id;
    this.closeDropdown();
    this.buildNavItems();
  }

  openFrom(item: NavItem) {
    if (!item.comingSoon) {
      if (item.opensLegacy) {
        this.openLegacy();
      } else if (item.opensMenu) {
        this.openMenu(item.opensMenu);
      }
    }
  }

  openLegacy() {
    this.sideNavService.setLegacyMenu(true);
  }

  backHome() {
    this.openMenu(MenuId.Home);
  }

  toggleExpanded() {
    this.sideNavService.setSideNavExpanded(!this.isExpanded);
  }

  syncMenuToUrl(url: string) {
    // Rows differing only by query string share a path, which is what menus match on.
    const path = url.split('?')[0];

    // Menus can share a route (Merchant Profiles), so the open menu keeps a url it lists and the pages under it.
    if (!menuClaimsUrl(this.activeMenu, path)) {
      const target = menuForUrl(path, this.router.parseUrl(url).queryParams);

      if (target !== this.activeMenu) {
        this.activeMenu = target;
        this.buildNavItems();
      }
    }
  }

  // An accordion parent has no route of its own, so routerLinkActive never marks it.
  isParentActive(item: NavItem): boolean {
    let active = false;

    if (item.children && item.children.length) {
      const url = this.router.url.split('?')[0];

      active = item.children.some((child: NavItem) => {
        const route = resolveRoute(child, this.navContext);
        let matches = false;

        if (route && route.length) {
          const path = routePath(route);

          matches = path !== '/' && urlIsUnder(url, path);
        }

        return matches;
      });
    }

    return active;
  }

  dynamicChildren(item: NavItem): ReportsUserConfiguration[] {
    let configs: ReportsUserConfiguration[] = [];

    switch (item.dynamicChildrenKey) {
      case DynamicChildrenKey.Reports:
        configs = this.reportsUserConfig;
        break;
      case DynamicChildrenKey.LegacyReports:
        configs = this.legacyReportsUserConfig;
        break;
      case DynamicChildrenKey.LegacyGlobalReports:
        configs = this.legacyReportsGlobalConfig;
        break;
      default:
        break;
    }

    return configs;
  }

  // The non-legacy report lists route by the config's own link; legacy lists share /report/.
  dynamicChildRoute(item: NavItem, config: ReportsUserConfiguration): any[] {
    let route: any[] = ['/report/', config.id];

    if (item.dynamicChildrenKey === DynamicChildrenKey.Reports) {
      route = [config.link, config.id];
    }

    return route;
  }

  toggleDropdownStyles(dropdownId: string) {
    this.subDropdownStylesApplied = '';
    if (this.dropdownStylesApplied === dropdownId) {
      this.selectedDropdown = '';
      this.dropdownStylesApplied = '';
    } else {
      this.dropdownStylesApplied = dropdownId;
      this.selectedDropdown = dropdownId;
    }
  }

  closeSubDropdown() {
    this.subDropdownStylesApplied = '';
  }

  toggleSubDropdownStyles(dropdownId: string) {
    if (this.subDropdownStylesApplied === dropdownId) {
      this.subDropdownStylesApplied = '';
    } else {
      this.subDropdownStylesApplied = dropdownId;
    }
  }

  onDropdownClick(event: Event) {
    event.stopPropagation();
  }

  isProfilerEnabled() {
    return this.storageService.get('profilerEnabled');
  }

  isCProfilerEnabled() {
    return this.storageService.get('cprofilerEnabled');
  }

  closeDropdown() {
    this.selectedDropdown = '';
    this.dropdownStylesApplied = '';
    this.subDropdownStylesApplied = '';
  }

  outSideClick() {
    if (!this.isExpanded) {
      this.selectedDropdown = '';
      this.dropdownStylesApplied = '';
      this.subDropdownStylesApplied = '';
    }
  }

  isMouseflowEnabled() {
    return this.mouseflowService.isEnabled()
  }
}
